import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Quote, Star, BadgeCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const FALLBACK_TESTIMONIALS = [
  {
    id: 1,
    patient_name: 'James W.',
    patient_country: 'United Kingdom',
    rating: 5,
    review: 'Honestly the best decision. The team picked me up at the airport, the hotel was beachfront and the implants are flawless after a year.',
    treatment: 'Full Mouth Implants',
  },
  {
    id: 2,
    patient_name: 'Linnea S.',
    patient_country: 'Norway',
    rating: 5,
    review: 'I was nervous about going abroad but the coordinator spoke Norwegian and explained every step. New smile in 7 days!',
    treatment: 'Hollywood Smile · 16 Veneers',
  },
  {
    id: 3,
    patient_name: 'Thomas R.',
    patient_country: 'Germany',
    rating: 5,
    review: 'Modernste Technik und sehr freundliches Team. Preis und Qualität unschlagbar gegenüber Deutschland.',
    treatment: 'Zirconium Crowns',
  },
  {
    id: 4,
    patient_name: 'Olga M.',
    patient_country: 'Russia',
    rating: 5,
    review: 'Очень внимательное отношение, всё прозрачно, без скрытых платежей. Рекомендую от всей души.',
    treatment: 'All-on-4 Implants',
  },
];

const FlagFor = (country) => {
  if (!country) return '';
  const c = country.toLowerCase();
  if (c.includes('united kingdom') || c.includes('england') || c.includes('uk')) return '🇬🇧';
  if (c.includes('germany') || c.includes('deutschland')) return '🇩🇪';
  if (c.includes('norway') || c.includes('norge')) return '🇳🇴';
  if (c.includes('russia') || c.includes('россия')) return '🇷🇺';
  if (c.includes('sweden')) return '🇸🇪';
  if (c.includes('netherlands')) return '🇳🇱';
  if (c.includes('finland')) return '🇫🇮';
  if (c.includes('denmark')) return '🇩🇰';
  if (c.includes('turkey') || c.includes('türk')) return '🇹🇷';
  return '🌍';
};

const TestimonialsSection = () => {
  const { t, currentLanguage } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/testimonials/featured`, {
          params: { lang: currentLanguage, limit: 6 },
        });
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setItems(list.length > 0 ? list : FALLBACK_TESTIMONIALS);
      } catch (err) {
        console.error('Failed to load testimonials:', err);
        if (!cancelled) setItems(FALLBACK_TESTIMONIALS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  const total = items.length;
  const go = (delta) => {
    if (total === 0) return;
    setActive((prev) => (prev + delta + total) % total);
  };

  return (
    <section id="testimonials" className="section-padding section-tone-b">
      <div className="container-premium">
        <div className="section-head">
          <span className="section-eyebrow">{t('testimonials.eyebrow', 'Patient Stories')}</span>
          <h2 className="section-title">{t('testimonials.title', 'What Our Patients Say')}</h2>
          <p className="section-sub">
            {t('testimonials.subtitle', 'Real reviews from international patients who trusted us with their smiles.')}
          </p>
        </div>

        {loading || total === 0 ? (
          <div className="testimonial-skeleton">
            <div className="skeleton-line w-30" />
            <div className="skeleton-line w-90" />
            <div className="skeleton-line w-80" />
            <div className="skeleton-line w-50" />
          </div>
        ) : (
          <>
            <div className="testimonial-stage">
              <button className="testimonial-nav prev" onClick={() => go(-1)} aria-label="Previous">
                <ChevronLeft size={18} />
              </button>
              <div className="testimonial-track" ref={trackRef}>
                {items.map((it, idx) => (
                  <article
                    key={it.id || idx}
                    className={`testimonial-card ${idx === active ? 'is-active' : ''}`}
                    aria-hidden={idx === active ? 'false' : 'true'}
                  >
                    <Quote className="testimonial-quote" size={28} />
                    <div className="testimonial-rating" aria-label={`${it.rating || 5} stars`}>
                      {Array.from({ length: it.rating || 5 }).map((_, i) => (
                        <Star key={i} size={14} fill="currentColor" />
                      ))}
                    </div>
                    <p className="testimonial-text">{it.review}</p>
                    <div className="testimonial-meta">
                      <strong>
                        {FlagFor(it.patient_country)} {it.patient_name}
                      </strong>
                      <span>
                        <BadgeCheck size={13} /> {t('testimonials.verified', 'Verified Patient')}
                        {it.treatment ? ` · ${it.treatment}` : ''}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
              <button className="testimonial-nav next" onClick={() => go(1)} aria-label="Next">
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="testimonial-dots">
              {items.map((_, dot) => (
                <button
                  key={dot}
                  type="button"
                  className={`testimonial-dot ${dot === active ? 'active' : ''}`}
                  onClick={() => setActive(dot)}
                  aria-label={`Go to testimonial ${dot + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
